"use client";

import { c as _c } from "react/compiler-runtime";
import { Eraser, Highlighter, ImagePlus, Minus, MousePointer2, PenLine, Square, Type } from "lucide-react";
import { useRef, useState } from "react";
import { inputClass, labelClass, primaryButtonClass } from "@/components/form";
import type { EditorMode, EditorStyle } from "@/components/pdf-editor/EditorStage";
import { EditorWorkspace } from "@/components/pdf-editor/EditorWorkspace";
import { ProgressBar } from "@/components/ProgressBar";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { useEditor } from "@/hooks/useEditor";
import { useToolFiles } from "@/hooks/useToolFiles";
import { acceptAttribute, IMAGE_KINDS, kindOf, outputName } from "@/lib/files";
import { readImageSize } from "@/lib/image/info";
import { isWinAnsi, type Item } from "@/lib/pdf/items";
import { FONT_GROUPS, FONTS, fontInfo, needsShaping, type FontId } from "@/lib/pdf/fonts";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
import { runImageJob } from "@/workers/image-client";
import { annotateFile, cssFont, loadScreenFont } from "@/workers/pdf-client";
import { shownPageSize } from "@/workers/pdf-render";
const tool = getTool("edit-pdf")!;
const TOOLS: {
  mode: EditorMode | "image";
  label: string;
  Icon: typeof Type;
}[] = [{
  mode: "select",
  label: "Select",
  Icon: MousePointer2
}, {
  mode: "text",
  label: "Text",
  Icon: Type
}, {
  mode: "image",
  label: "Picture",
  Icon: ImagePlus
}, {
  mode: "pen",
  label: "Pen",
  Icon: PenLine
}, {
  mode: "highlight",
  label: "Highlighter",
  Icon: Highlighter
}, {
  mode: "line",
  label: "Line",
  Icon: Minus
}, {
  mode: "rect",
  label: "Box",
  Icon: Square
}, {
  mode: "whiteout",
  label: "White-out",
  Icon: Eraser
}];

// Ready-made colours; the rainbow button after them opens a picker for any colour.
const COLORS = ["#0f1222",
// black
"#545a72",
// grey
"#ffffff",
// white
"#c8203f",
// red
"#e8590c",
// orange
"#f5b700",
// yellow
"#2f9e44",
// green
"#087a70",
// teal
"#2357d8",
// blue
"#4f3bef",
// indigo
"#9c36b5",
// purple
"#d6336c",
// pink
"#8b5e34" // brown
];
const HIGHLIGHTS = ["#ffd500", "#7ce07c", "#ff9ed2", "#7cc8ff", "#ffa94d", "#c3a6ff"];

/** Text that will be saved as a picture instead of real text (a rough check:
 *  the save step also checks the font's letters). */
const mayBePicture = (font: FontId, text: string) => fontInfo(font).file ? needsShaping(text) : !isWinAnsi(text);
const HINTS: Record<EditorMode, string> = {
  select: "Tap something you added to move or resize it.",
  text: "Tap the page where the text should go.",
  pen: "Draw on the page with your mouse or finger.",
  highlight: "Draw over text to highlight it.",
  line: "Drag to draw a straight line.",
  rect: "Drag to draw a box.",
  whiteout: "Drag over anything you want to hide, then add new text on top."
};

/** FR-13: add text, images, shapes, lines, pen, highlighter, white-out. */
export function EditTool() {
  const {
    files,
    setFiles,
    notices
  } = useToolFiles(tool);
  const file = files[0];
  const editor = useEditor();
  const [page, setPage] = useState(0);
  const [mode, setMode] = useState<EditorMode>("select");
  const [style, setStyle] = useState<EditorStyle>({
    color: "#0f1222",
    strokeWidth: 3,
    highlightColor: "#ffd500",
    highlightWidth: 14,
    font: "sans",
    fontSize: 16,
    fill: null
  });
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const picker = useRef<HTMLInputElement>(null);
  const textBox = useRef<HTMLTextAreaElement>(null);
  const selected = editor.selected;
  function restart() {
    editor.reset();
    setPage(0);
    setMode("select");
    setResults(null);
    setError("");
  }
  async function addPicture(picked: File | undefined) {
    if (!picked || !file) return;
    setError("");
    try {
      // The PDF can hold JPG and PNG; anything else is converted to PNG first.
      const kind = kindOf(picked);
      const blob = kind === "jpg" || kind === "png" ? picked : (await runImageJob(picked, {
        format: "png"
      })).blob;
      const head = new Uint8Array(await blob.slice(0, 256 * 1024).arrayBuffer());
      const natural = readImageSize(head) ?? {
        width: 200,
        height: 150
      };
      const pageSize = await shownPageSize(file, page);
      const width = Math.min(pageSize.width * 0.4, natural.width * 0.75);
      const height = width * natural.height / natural.width;
      editor.add({
        page,
        kind: "image",
        blob,
        format: blob.type === "image/jpeg" ? "jpg" : "png",
        x: (pageSize.width - width) / 2,
        y: (pageSize.height - height) / 2,
        width,
        height
      });
      setMode("select");
    } catch (e) {
      setError(`That picture couldn't be added: ${(e as Error).message}`);
    }
  }
  async function save() {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const blob_0 = await annotateFile(file, editor.items);
      setResults([{
        blob: blob_0,
        name: outputName(file.name, "edited", "pdf")
      }]);
    } catch (e_0) {
      setError((e_0 as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const update = (item: Item, typing?: string) => editor.update(item, typing);
  const swatch = (color: string, active: boolean, onClick: () => void, label: string) => <button key={color} type="button" aria-label={label} aria-pressed={active} onClick={onClick} className={cn("flex size-11 items-center justify-center rounded-full border-2", active ? "border-brand" : "border-transparent")}>
      <span className="size-7 rounded-full border border-line-strong" style={{
      background: color
    }} />
    </button>;

  /** Colour swatches plus a picker for any other colour. */
  const palette = (colors: string[], current: string, pick: (c: string, live?: boolean) => void, name = "Colour") => <ColorRow label={name}>
      {colors.map(c => swatch(c, current === c, () => pick(c), `${name} ${c}`))}
      <label className={cn("relative flex size-11 cursor-pointer items-center justify-center rounded-full border-2 focus-within:outline-2 focus-within:outline-ring", colors.includes(current) ? "border-transparent" : "border-brand")} title="Pick any colour">
        <span className="size-7 rounded-full border border-line-strong" style={{
        background: "conic-gradient(#f03e3e, #f5b700, #2f9e44, #22b8cf, #2357d8, #9c36b5, #f03e3e)"
      }} />
        <input type="color" aria-label={`Custom ${name.toLowerCase()}`} value={current} onChange={e_1 => pick(e_1.target.value, true)} className="absolute inset-0 size-full cursor-pointer opacity-0" />
      </label>
    </ColorRow>;

  /** Fonts grouped like a word processor's list; Google fonts load on demand. */
  const fontSelect = (value: FontId, choose: (id: FontId) => void) => <label className={cn(labelClass, "flex-1")}>
      Font
      <select className={inputClass} value={value} style={{
      fontFamily: cssFont(value)
    }} onChange={e_2 => {
      const id = e_2.target.value as FontId;
      choose(id);
      loadScreenFont(id).catch(() => setError(`The font “${fontInfo(id).label}” couldn't be loaded. Check your connection and try again.`));
    }}>
        {FONT_GROUPS.map(group => <optgroup key={group} label={group}>
            {FONTS.filter(f => f.group === group).map(f_0 => <option key={f_0.id} value={f_0.id}>
                {f_0.label}
              </option>)}
          </optgroup>)}
      </select>
    </label>;

  // Settings for the selected item, or for the next thing to be drawn.
  function panel() {
    if (selected?.kind === "text") {
      return <div className="flex flex-col gap-3">
          <label className={labelClass}>
            Text
            <textarea ref={textBox} rows={3} className={cn(inputClass, "py-2")} value={selected.text} onChange={e_3 => update({
            ...selected,
            text: e_3.target.value
          }, "text")} />
          </label>
          {mayBePicture(selected.font, selected.text) && <p className="text-sm text-ink">
              This text uses letters this font can&apos;t write into a PDF (for
              example Hindi or Arabic), so it will be saved as a picture of the
              text.
            </p>}
          <div className="flex gap-2">
            {fontSelect(selected.font, font => update({
            ...selected,
            font
          }))}
            <label className={cn(labelClass, "w-24")}>
              Size
              <input className={inputClass} inputMode="numeric" value={selected.size} onChange={e_4 => {
              const size = Number(e_4.target.value);
              if (size >= 4 && size <= 200) update({
                ...selected,
                size
              }, "size");
            }} />
            </label>
          </div>
          {palette(COLORS, selected.color, (color_0, live) => update({
          ...selected,
          color: color_0
        }, live ? "color" : undefined))}
        </div>;
    }
    if (selected && (selected.kind === "pen" || selected.kind === "line" || selected.kind === "rect")) {
      const color_1 = selected.kind === "rect" ? selected.stroke : selected.color;
      return palette(COLORS, color_1, (c_0, live_0) => update(selected.kind === "rect" ? {
        ...selected,
        stroke: c_0
      } : {
        ...selected,
        color: c_0
      }, live_0 ? "color" : undefined));
    }
    if (selected) {
      return <p className="text-sm text-ink">
          Drag to move it, or use the corner handles to resize it.
        </p>;
    }
    return <div className="flex flex-col gap-3">
        <p className="text-sm text-ink">{HINTS[mode]}</p>
        {mode === "highlight" ? palette(HIGHLIGHTS, style.highlightColor, c_1 => setStyle({
        ...style,
        highlightColor: c_1
      }), "Highlight") : mode !== "whiteout" && mode !== "select" ? palette(COLORS, style.color, c_2 => setStyle({
        ...style,
        color: c_2
      })) : null}
        {(mode === "pen" || mode === "line" || mode === "rect") && <label className={labelClass}>
            Thickness: {style.strokeWidth} pt
            <input type="range" min={1} max={12} value={style.strokeWidth} onChange={e_5 => setStyle({
          ...style,
          strokeWidth: Number(e_5.target.value)
        })} className="h-11 accent-brand" />
          </label>}
        {mode === "text" && <div className="flex gap-2">
            {fontSelect(style.font, font_0 => setStyle({
          ...style,
          font: font_0
        }))}
            <label className={cn(labelClass, "w-24")}>
              Size
              <input className={inputClass} inputMode="numeric" defaultValue={style.fontSize} onChange={e_6 => {
            const fontSize = Number(e_6.target.value);
            if (fontSize >= 4 && fontSize <= 200) setStyle({
              ...style,
              fontSize
            });
          }} />
            </label>
          </div>}
      </div>;
  }
  return <ToolShell tool={tool} files={files} onFilesChange={next => {
    setFiles(next);
    restart();
  }} notices={notices} results={results} onStartOver={() => {
    setFiles([]);
    restart();
  }}>
      {file && <EditorWorkspace file={file} editor={editor} page={page} onPageChange={p => {
      setPage(p);
      editor.select(null);
    }} mode={mode} style={style} onCreated={item_0 => {
      // After placing text, switch to Select so it can be typed straight away.
      if (item_0.kind === "text") {
        setMode("select");
        requestAnimationFrame(() => textBox.current?.select());
      }
    }} toolbar={<div role="toolbar" aria-label="Add to the page" className="flex flex-wrap gap-1.5">
              {TOOLS.map(({
        mode: m,
        label: label_0,
        Icon
      }) => <button key={m} type="button" aria-pressed={m === mode} onClick={() => {
        if (m === "image") picker.current?.click();else {
          setMode(m);
          if (m !== "select") editor.select(null);
        }
      }} className={cn("inline-flex min-h-11 items-center gap-1.5 rounded-lg border-[1.5px] px-2.5 text-sm font-medium", m === mode ? "border-brand bg-brand-tint text-brand-strong" : "border-line-strong bg-card hover:bg-canvas")}>
                  <Icon className="size-4" aria-hidden="true" />
                  {label_0}
                </button>)}
              <input ref={picker} type="file" className="sr-only" tabIndex={-1} aria-hidden="true" data-testid="picture-input" accept={acceptAttribute(IMAGE_KINDS)} onChange={e_7 => {
        addPicture(e_7.target.files?.[0]);
        e_7.target.value = "";
      }} />
            </div>} panel={panel()} footer={<section aria-label="Save" className="z-20 flex flex-col gap-3 rounded-[16px] border bg-card p-4 sm:sticky sm:bottom-0 sm:flex-row sm:items-center sm:justify-between sm:shadow-[0_-4px_12px_rgba(31,36,48,0.06)]">
              <div className="flex flex-col text-sm">
                <span className="font-semibold">
                  {editor.items.length}{" "}
                  {editor.items.length === 1 ? "thing" : "things"} added
                </span>
                <span className="text-muted-foreground">
                  Existing text in the PDF can&apos;t be rewritten — cover it
                  with white-out and type over it. Nothing is uploaded.
                </span>
                {error && <span role="alert" className="font-medium text-destructive">
                    {error}
                  </span>}
              </div>
              {busy ? <div className="sm:w-64">
                  <ProgressBar label="Saving your PDF…" />
                </div> : <button type="button" onClick={save} disabled={editor.items.length === 0} className={cn(primaryButtonClass, "sm:w-auto")}>
                  Save PDF
                </button>}
            </section>} />}
    </ToolShell>;
}
function ColorRow(t0) {
  const $ = _c(3);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== label) {
    t1 = <div role="group" aria-label={label} className="flex flex-wrap gap-1">{children}</div>;
    $[0] = children;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}